import { rgbCss } from './rgbCss';

export const ASCII_CHARSETS = {
    standard: {
        id: 'standard',
        label: 'Standard',
        ramp: '@%#*+=-:. ',
    },
    block: {
        id: 'block',
        label: 'Block',
        ramp: '█▓▒░ ',
    },
    extended: {
        id: 'extended',
        label: 'Extended',
        ramp: '$@B%8&WM#*oahkbdpqwmZO0QLCJUYXzcvunxrjft/\\|()1{}[]?-_+~<>i!lI;:,"^`\'. ',
    },
    minimal: {
        id: 'minimal',
        label: 'Minimal',
        ramp: '@+. ',
    },
    binary: {
        id: 'binary',
        label: 'Binary',
        ramp: '10',
    },
    digits: {
        id: 'digits',
        label: 'Digits',
        ramp: '9876543210 ',
    },
    custom: {
        id: 'custom',
        label: 'Custom',
        ramp: '',
    },
};

export const ASCII_CHARSET_OPTIONS = Object.values(ASCII_CHARSETS).map(({ id, label }) => ({
    id,
    label,
}));

export const ASCII_COLOR_MODES = [
    { id: 'mono', label: 'Mono' },
    { id: 'colored', label: 'Colored' },
    { id: 'palette', label: 'Palette' },
];

export const DEFAULT_ASCII_MONO_COLOR = { r: 232, g: 232, b: 232 };

/** Canonical ASCII option defaults — single source for options, UI, and engine fallbacks. */
export const DEFAULT_ASCII_OPTIONS = {
    asciiCharset: 'standard',
    asciiCustomCharset: ASCII_CHARSETS.standard.ramp,
    asciiColorMode: 'colored',
    asciiInvert: false,
};

const ASCII_FONT_FAMILY = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace';

export function resolveAsciiRamp(charsetId, customCharset = '') {
    if (charsetId === 'custom') {
        const custom = String(customCharset || '');
        return custom.length > 0 ? custom : ASCII_CHARSETS.standard.ramp;
    }

    return ASCII_CHARSETS[charsetId]?.ramp || ASCII_CHARSETS.standard.ramp;
}

export function calculateLuminance(r, g, b) {
    return (0.299 * r) + (0.587 * g) + (0.114 * b);
}

export function luminanceToChar(luminance, ramp, inverted = false) {
    if (!ramp || ramp.length === 0) {
        return ' ';
    }

    const normalized = Math.max(0, Math.min(1, luminance / 255));
    const value = inverted ? 1 - normalized : normalized;
    const index = Math.min(ramp.length - 1, Math.floor(value * ramp.length));
    return ramp[index];
}

/**
 * Prepare canvas text state once per ASCII pass. Returns a cell drawer that
 * only updates font size when an edge cell differs from the base tile size.
 */
export function prepareAsciiPass(ctx, tileSize, gap) {
    const baseFontSize = Math.max(4, tileSize - gap);
    let currentFontSize = baseFontSize;

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `${baseFontSize}px ${ASCII_FONT_FAMILY}`;

    return (x, y, cellW, cellH, char, color) => {
        if (!char || char === ' ') {
            return;
        }

        const fontSize = (cellW === tileSize && cellH === tileSize)
            ? baseFontSize
            : Math.max(4, Math.min(cellW, cellH) - gap);

        if (fontSize !== currentFontSize) {
            ctx.font = `${fontSize}px ${ASCII_FONT_FAMILY}`;
            currentFontSize = fontSize;
        }

        ctx.fillStyle = rgbCss(color);
        ctx.fillText(char, x + (cellW / 2), y + (cellH / 2));
    };
}
